import type { ExtractInjectionAPI } from '@atlaskit/editor-common/types';
import {
	admitAndRetainEditorRemixLifecycle,
	type EditorRemixLifecycleOwner,
	type RetainedEditorRemixLifecycle,
} from '@atlassian/malleable-ui-components/editor-remix/lifecycle-owner';
import type {
	EditorRemixLifecycleAdapter,
	EditorRemixLifecycleArtifactType,
	EditorRemixLifecycleEvent,
	EditorRemixLifecycleObservation,
} from '@atlassian/malleable-ui-components/types';

import type { MalleableUiPlugin } from '../../malleableUiPluginType';

type EditorApi = ExtractInjectionAPI<MalleableUiPlugin> | undefined;
type InlineRovoEditAttempt = {
	artifactType: EditorRemixLifecycleArtifactType;
	claimed: boolean;
	lifecycle: RetainedEditorRemixLifecycle;
	presentationStarted: boolean;
	targetId: string;
};
type Entry = {
	active?: InlineRovoEditAttempt;
	adapter?: EditorRemixLifecycleAdapter;
	owner?: EditorRemixLifecycleOwner;
};

export type InlineRovoEditOutcome =
	| 'handoff'
	| 'insertion-failed'
	| 'inserted'
	| 'generation-error'
	| 'generation-aborted'
	| 'result-ready'
	| 'direct-insertion-started';

export type BeginInlineRovoEditResult =
	| { attemptId: string; status: 'admitted' }
	| { status: 'legacy' | 'rejected' | 'unavailable' };

export type InlineRovoEditProducerAttemptObservation = {
	admissionHook: 'available' | 'unavailable';
	artifactType: 'image' | 'maui' | 'unknown';
	outcome: 'admitted' | 'legacy' | 'rejected' | 'unavailable';
	target: 'present' | 'missing';
};

const entries = new WeakMap<object, Entry>();

const entryFor = (api: EditorApi): Entry | undefined => {
	if (!api) {
		return undefined;
	}
	const existing = entries.get(api);
	if (existing) {
		return existing;
	}
	const entry: Entry = {};
	entries.set(api, entry);
	return entry;
};

const observe = (entry: Entry | undefined, observation: EditorRemixLifecycleObservation): void => {
	try {
		entry?.adapter?.observeLifecycle?.(observation);
	} catch {
		// Ownership observations must never affect editor behavior.
	}
};

export const registerEditorInlineRovoEditLifecycleAdapter = (
	api: EditorApi,
	adapter: EditorRemixLifecycleAdapter,
): (() => void) => {
	const entry = entryFor(api);
	if (!entry) {
		return () => {};
	}
	entry.adapter = adapter;
	return () => {
		if (entry.adapter === adapter) {
			entry.adapter = undefined;
			entry.active?.lifecycle.end('aborted');
			entry.active = undefined;
		}
	};
};

export const registerEditorInlineRovoEditLifecycleOwner = (
	api: EditorApi,
	owner: EditorRemixLifecycleOwner,
): (() => void) => {
	const entry = entryFor(api);
	if (!entry) {
		return () => {};
	}
	entry.owner = owner;
	return () => {
		if (entry.owner === owner) {
			entry.owner = undefined;
			entry.active?.lifecycle.end('aborted');
			entry.active = undefined;
		}
	};
};

/** Malleable UI's synchronous admission boundary. No rollout controls are read below this point. */
export const beginEditorInlineRovoEdit = (
	api: EditorApi,
	targetId: string | undefined,
): BeginInlineRovoEditResult => {
	const entry = entryFor(api);
	if (!entry?.adapter || !entry.owner) {
		return { status: 'unavailable' as const };
	}
	if (!targetId) {
		return { status: 'legacy' as const };
	}
	if (entry.active) {
		return { status: 'rejected' as const };
	}
	const lifecycleRetention = (() => {
		try {
			return admitAndRetainEditorRemixLifecycle({
				adapter: entry.adapter,
				owner: entry.owner,
				request: {
					kind: 'update',
					conversationId: targetId,
					targetNodeId: targetId,
					updateSentinel: targetId,
					observation: {
						producer: 'inline-rovo-edit',
						artifactType: 'unknown',
						sentinelSource: 'none',
					},
				},
			});
		} catch {
			return undefined;
		}
	})();
	if (!lifecycleRetention) {
		return { status: 'unavailable' as const };
	}
	if (lifecycleRetention.status === 'active-flow-rejected') {
		return { status: 'rejected' as const };
	}
	if (lifecycleRetention.status !== 'retained') {
		return lifecycleRetention.status === 'infrastructure-failure'
			? { status: 'unavailable' as const }
			: { status: 'legacy' as const };
	}
	const attemptId = lifecycleRetention.lifecycle.remixId;
	entry.active = {
		claimed: false,
		lifecycle: lifecycleRetention.lifecycle,
		presentationStarted: false,
		targetId,
		artifactType: 'unknown',
	};
	return { status: 'admitted' as const, attemptId };
};

/** Claims one Chat-handoff presentation start for the matching retained attempt. */
export const startEditorInlineRovoEditHandoffPresentation = (
	api: EditorApi,
	attemptId: string,
): boolean => {
	const active = entryFor(api)?.active;
	if (!active || active.lifecycle.remixId !== attemptId || active.presentationStarted) {
		return false;
	}
	active.presentationStarted = true;
	return true;
};

/** Claims a matching target exactly once before bridge callbacks are captured. */
export const claimEditorInlineRovoEdit = (
	api: EditorApi,
	targetId: string | undefined,
): string | undefined => {
	const entry = entryFor(api);
	const active = entry?.active;
	if (!active) {
		observe(entry, {
			actionSubjectId: 'bridgeClaim',
			producer: 'inline-rovo-edit',
			artifactType: 'unknown',
			outcome: 'no-retained-attempt',
			reason: 'no-active-attempt',
		});
		return undefined;
	}
	if (!targetId) {
		observe(entry, {
			actionSubjectId: 'bridgeClaim',
			producer: 'inline-rovo-edit',
			artifactType: active.artifactType,
			outcome: 'missing-target',
			reason: 'target-missing',
		});
		return undefined;
	}
	if (active.targetId !== targetId) {
		observe(entry, {
			actionSubjectId: 'bridgeClaim',
			producer: 'inline-rovo-edit',
			artifactType: active.artifactType,
			outcome: 'mismatched-target',
			reason: 'target-mismatch',
		});
		return undefined;
	}
	if (active.claimed) {
		observe(entry, {
			actionSubjectId: 'bridgeClaim',
			producer: 'inline-rovo-edit',
			artifactType: active.artifactType,
			outcome: 'no-retained-attempt',
			reason: 'already-claimed',
		});
		return undefined;
	}
	active.claimed = true;
	observe(entry, {
		actionSubjectId: 'bridgeClaim',
		producer: 'inline-rovo-edit',
		artifactType: active.artifactType,
		outcome: 'matched',
		reason: 'target-match',
	});
	return active.lifecycle.remixId;
};

export const observeEditorInlineRovoEditProducerAttempt = (
	api: EditorApi,
	observation: InlineRovoEditProducerAttemptObservation,
): void => {
	const entry = entryFor(api);
	// A concurrent rejected/unavailable submission is not the retained attempt and must not
	// relabel the active flow's later bridge/settlement observations.
	if (entry?.active && observation.outcome === 'admitted') {
		entry.active.artifactType = observation.artifactType;
	}
	const reason =
		observation.target === 'present'
			? observation.admissionHook === 'available'
				? 'target-present-admission-available'
				: 'target-present-admission-unavailable'
			: observation.admissionHook === 'available'
				? 'target-missing-admission-available'
				: 'target-missing-admission-unavailable';
	// A rejection never replaces `entry.active`, so it is still the attempt that blocked this one.
	// With no active inline attempt, the owner refused because a create or variant flow is live.
	const blockingAttempt =
		observation.outcome !== 'rejected'
			? undefined
			: !entry?.active
				? 'other-flow'
				: entry.active.claimed
					? 'inline-claimed'
					: entry.active.presentationStarted
						? 'inline-unclaimed-handed-off'
						: 'inline-unclaimed';
	observe(entry, {
		actionSubjectId: 'producerAttempt',
		producer: 'inline-rovo-edit',
		artifactType: observation.artifactType,
		outcome: observation.outcome,
		reason,
		...(blockingAttempt ? { blockingAttempt } : {}),
	});
};

/** Releases completed replies owned by another path, without interrupting claimed processors. */
export const settleUnclaimedEditorInlineRovoEdit = (api: EditorApi, attemptId: string): boolean => {
	const entry = entryFor(api);
	const active = entry?.active;
	if (!active || active.lifecycle.remixId !== attemptId || active.claimed) {
		return false;
	}
	return reportEditorInlineRovoEdit(api, attemptId, 'handoff');
};

export const reportEditorInlineRovoEdit = (
	api: EditorApi,
	attemptId: string,
	result: InlineRovoEditOutcome,
): boolean => {
	const entry = entryFor(api);
	const active = entry?.active;
	if (!active || active.lifecycle.remixId !== attemptId) {
		return false;
	}
	const transition = (event: EditorRemixLifecycleEvent) => active.lifecycle.transition(event);
	if (result === 'result-ready') {
		return transition({ type: 'RESULT_READY' });
	}
	if (result === 'direct-insertion-started') {
		return transition({ type: 'DIRECT_INSERTION_STARTED' });
	}
	if (result === 'handoff') {
		if (!active.lifecycle.end('handed-off')) {
			return false;
		}
	} else if (result === 'inserted') {
		if (!transition({ type: 'INSERTED' })) {
			return false;
		}
	} else if (result === 'insertion-failed') {
		if (!transition({ type: 'INSERTION_FAILED' })) {
			return false;
		}
	} else if (result === 'generation-error') {
		if (!transition({ type: 'GENERATION_ERROR' })) {
			return false;
		}
	} else if (!transition({ type: 'GENERATION_ABORTED' })) {
		return false;
	}
	entry.active = undefined;
	return true;
};
